import { useState, type FormEvent } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import type { DeskDetail, DeskPatchBody, InboundMode } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { deskDetailOptions, errorMessage } from '@/features/desks/desks-queries';
import { SettingsBreadcrumb } from '@/features/settings/settings-ui';
import { DeskActiveDot } from '@/features/desks/DesksPage';
import { DeskStagesSection } from '@/features/desks/desk-stages-section';
import { DeskPrioritiesSection } from '@/features/desks/desk-priorities-section';
import { DeskStatusesSection } from '@/features/desks/desk-statuses-section';
import { DeskCloseReasonsSection } from '@/features/desks/desk-close-reasons-section';
import { DeskScheduleSection } from '@/features/desks/desk-schedule-section';
import { DeskCatalogsSection } from '@/features/desks/desk-catalogs-section';
import { DeskGroupsSection } from '@/features/desks/desk-groups-section';
import { DeskFormSection } from '@/features/desks/desk-form-section';
import { DeskOriginBadge, DeskOriginSection } from '@/features/desks/desk-origin-section';
import { DeskNatureSection } from '@/features/desks/desk-nature-section';
import { DeskDispatchSection } from '@/features/desks/desk-dispatch-section';

const routeApi = getRouteApi('/_authenticated/settings/desks/$deskId');

function SettingsSkeleton() {
  return (
    <div className="space-y-4" aria-busy>
      <div className="rounded-lg border bg-card p-4 shadow-xs">
        <Skeleton className="h-3.5 w-32" />
        <Skeleton className="mt-3 h-6 w-64" />
      </div>
      <div className="grid items-start gap-4 xl:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="space-y-3 rounded-lg border bg-card p-4 shadow-xs">
            <Skeleton className="h-4 w-24" />
            {Array.from({ length: 4 }).map((_, j) => (
              <Skeleton key={j} className="h-9 w-full" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function DeskSettingsPage() {
  const { deskId } = routeApi.useParams();
  const detail = useQuery(deskDetailOptions(Number(deskId)));
  const [editOpen, setEditOpen] = useState(false);

  if (detail.isPending) return <SettingsSkeleton />;
  if (detail.isError) {
    return (
      <div className="rounded-lg border bg-card shadow-xs">
        <EmptyState message={`Erro ao carregar mesa: ${errorMessage(detail.error)}`} />
      </div>
    );
  }
  const desk = detail.data;

  return (
    <div className="space-y-4">
      {/* Cabeçalho */}
      <div className="rounded-lg border bg-card px-4 py-3 shadow-xs">
        <SettingsBreadcrumb
          parent={{ label: 'Mesas de serviço', to: '/settings/desks' }}
          page={desk.displayName}
        />
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
          <h1 className="max-w-full truncate text-xl leading-7 font-semibold">
            {desk.displayName}
          </h1>
          {desk.name !== desk.displayName ? (
            <span className="hidden max-w-64 truncate text-sm text-muted-foreground sm:inline">
              {desk.name}
            </span>
          ) : null}
          <DeskActiveDot active={desk.active} />
          <DeskOriginBadge origin={desk.origin} />
          <div className="ml-auto">
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil className="size-3.5" />
              Editar
            </Button>
          </div>
        </div>
      </div>

      {/* spec 011: quem manda nos tickets — antes de estágios/prioridades, que
          numa mesa espelhada são reescritos pelo sync */}
      <DeskOriginSection desk={desk} />
      {/* spec 017: natureza do serviço (tipo da OS, indicadores do portal) */}
      <DeskNatureSection desk={desk} />

      <div className="grid items-start gap-4 xl:grid-cols-3">
        <DeskStagesSection desk={desk} />
        <DeskPrioritiesSection desk={desk} />
        <DeskStatusesSection desk={desk} />
      </div>

      {/* spec 054: com motivo ativo, fechar ticket desta mesa pede um deles */}
      <div className="grid items-start gap-4 xl:grid-cols-3">
        <DeskCloseReasonsSection deskId={desk.id} />
      </div>

      <div className="grid items-start gap-4 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <DeskScheduleSection deskId={desk.id} />
        </div>
        <DeskCatalogsSection deskId={desk.id} />
      </div>

      {/* Automações, atividades agendadas e checklists moraram aqui até a v8 —
          agora são telas cross-mesa em Configurações / Mesa de serviço. */}
      <div className="grid items-start gap-4 xl:grid-cols-2">
        <DeskGroupsSection deskId={desk.id} />
        <DeskFormSection desk={desk} />
      </div>

      {/* spec 014: disparo por ticket — a regra depende dos estágios acima */}
      <DeskDispatchSection desk={desk} />

      {editOpen ? <EditDeskDialog desk={desk} onClose={() => setEditOpen(false)} /> : null}
    </div>
  );
}

/** local-part válido do alias de abertura por e-mail (espelha o shared). */
const SLUG_RE = /^[a-z0-9](?:[a-z0-9._-]*[a-z0-9])?$/;

function EditDeskDialog({ desk, onClose }: { desk: DeskDetail; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(desk.name);
  const [displayName, setDisplayName] = useState(desk.displayName);
  const [active, setActive] = useState(desk.active);
  const [inboundEmailSlug, setInboundEmailSlug] = useState(desk.inboundEmailSlug ?? '');
  const [inboundMode, setInboundMode] = useState<InboundMode>(desk.inboundMode);
  const [openingTemplate, setOpeningTemplate] = useState(desk.openingTemplate ?? '');
  const [portalEnabled, setPortalEnabled] = useState(desk.portalEnabled);
  const [clientVisible, setClientVisible] = useState(desk.clientVisible);
  const [surveyEnabled, setSurveyEnabled] = useState(desk.surveyEnabled);

  const slug = inboundEmailSlug.trim().toLowerCase();
  const slugInvalid = slug !== '' && !SLUG_RE.test(slug);

  const save = useMutation({
    mutationFn: async (body: DeskPatchBody) => {
      const res = await api<DeskDetail>(`/desks/${desk.id}`, { method: 'PATCH', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['erp', 'desks'] });
      onClose();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !displayName.trim() || slugInvalid) return;
    save.mutate({
      name: name.trim(),
      displayName: displayName.trim(),
      active,
      inboundEmailSlug: slug || null,
      inboundMode,
      openingTemplate: openingTemplate.trim() ? openingTemplate : null,
      // abrir sem ver criaria ticket que some da tela do cliente: o backend
      // recusa abrir em mesa oculta, e aqui a abertura só vai junto com a visão
      portalEnabled: clientVisible && portalEnabled,
      clientVisible,
      surveyEnabled,
    });
  };

  return (
    <Dialog open onClose={onClose} title="Editar mesa">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="edit-desk-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="edit-desk-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="edit-desk-display-name">
            Nome de exibição <span className="text-destructive">*</span>
          </Label>
          <Input
            id="edit-desk-display-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
          />
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
          <Switch checked={active} onCheckedChange={setActive} />
          Mesa ativa
        </label>

        <div className="space-y-3 border-t pt-3">
          <div className="space-y-1.5">
            <Label htmlFor="edit-desk-inbound-slug">Slug de e-mail de abertura</Label>
            <Input
              id="edit-desk-inbound-slug"
              value={inboundEmailSlug}
              onChange={(e) => setInboundEmailSlug(e.target.value)}
              placeholder="suporte"
              aria-invalid={slugInvalid}
            />
            {slugInvalid ? (
              <p className="text-xs text-destructive">
                Use apenas letras minúsculas, números e &quot;.&quot;, &quot;_&quot; ou
                &quot;-&quot; (sem começar nem terminar com eles).
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                E-mails enviados para{' '}
                <code className="rounded bg-muted px-1 py-0.5">
                  {slug || '<slug>'}@…
                </code>{' '}
                abrem tickets nesta mesa. Vazio = mesa não recebe e-mail.
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-desk-inbound-mode">Entrada de e-mail</Label>
            <Select
              id="edit-desk-inbound-mode"
              value={inboundMode}
              onChange={(e) => setInboundMode(e.target.value as InboundMode)}
            >
              <option value="direct">Direto</option>
              <option value="preticket">Caixa de pré-tickets</option>
            </Select>
            <p className="text-xs text-muted-foreground">
              {inboundMode === 'direct'
                ? 'E-mails novos abrem tickets automaticamente.'
                : 'E-mails novos caem em Pré-tickets para triagem (aceitar ou descartar).'}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-desk-opening-template">Modelo de abertura</Label>
            <Textarea
              id="edit-desk-opening-template"
              value={openingTemplate}
              onChange={(e) => setOpeningTemplate(e.target.value)}
              rows={4}
              placeholder="Texto que pré-preenche a descrição de novos tickets desta mesa…"
            />
            <p className="text-xs text-muted-foreground">
              Pré-preenche a descrição de tickets novos da mesa (app e portal). Vazio = sem
              modelo.
            </p>
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
            <Switch checked={clientVisible} onCheckedChange={setClientVisible} />
            Visível para o cliente no portal
          </label>
          <p className="-mt-2 pl-10 text-xs text-muted-foreground">
            O cliente acompanha os tickets dele desta mesa: título, situação, descrição e
            mensagens públicas. Deixe desligado em mesas internas (financeiro, estoque,
            pagamento de prestador).
          </p>
          <label
            className={`flex items-center gap-2 text-sm font-medium ${clientVisible ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
          >
            <Switch
              checked={clientVisible && portalEnabled}
              onCheckedChange={setPortalEnabled}
              disabled={!clientVisible}
            />
            Cliente pode abrir ticket pelo portal
          </label>
          <p className="-mt-2 pl-10 text-xs text-muted-foreground">
            Só em mesa visível para o cliente e operada no ERP.
          </p>
          <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
            <Switch checked={surveyEnabled} onCheckedChange={setSurveyEnabled} />
            Avaliação de atendimento (CSAT)
          </label>
          <p className="-mt-2 pl-10 text-xs text-muted-foreground">
            Envia pesquisa de satisfação por e-mail ao solicitante quando o ticket é fechado.
          </p>
        </div>

        {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error)}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={save.isPending || !name.trim() || !displayName.trim() || slugInvalid}
          >
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
